import type { Role } from '@attendance/shared';
import type { FeatureFlags, FeatureKey } from '@attendance/shared/features';
import { useAuthStore } from '../../stores/authStore.js';
import { defaultsFor, heldPermissions } from '../../hooks/usePermission.js';
import { useBrandingStore } from '../../stores/brandingStore.js';
import type { NavLeaf } from './navItems.js';

/* ===========================================================================
 * WHO SEES A NAV ROW, AND WHETHER A ROW IS THE PAGE YOU ARE ON.
 *
 * Lifted out of `navItems.ts`, which is the LIST. These are the predicates
 * applied to it: they name no row, and every one takes what it needs as an
 * argument. `NavLeaf` comes back the other way as a TYPE-ONLY import, which
 * is erased at compile time — so the two files do not close a runtime cycle,
 * and the note in `navItems.ts` about not reaching back for one string still
 * holds.
 *
 * `navItems.ts` re-exports all of these, so the call sites that import them
 * from there keep working.
 * ======================================================================== */

/**
 * Role + permission gate for a single nav item. A row carrying `permission`
 * shows only to someone who holds it; when it carries `roles` too, both apply.
 * `permissions` defaults to the signed-in user's, read from the store. Where
 * neither the caller nor the user supplies a list, the viewer holds what their
 * `role` holds out of the box, so a call site (or fixture) that names only a
 * role sees the built-in behaviour.
 */
export function canSee(
  item: { roles?: Role[]; permission?: string | readonly string[] },
  role: Role | undefined,
  permissions?: readonly string[]
): boolean {
  if (item.roles && !(role && item.roles.includes(role))) return false;
  if (!item.permission) return true;
  const user = useAuthStore.getState().user;
  const held =
    permissions ??
    (user && (!role || user.role === role) ? heldPermissions(user) : undefined) ??
    (role ? defaultsFor(role) : undefined);
  const need = typeof item.permission === 'string' ? [item.permission] : item.permission;
  return !!held && need.some((k) => held.includes(k));
}

/** Feature gate for a single nav item. Untagged items are always on; a tagged
 * item shows only when its flag is not explicitly off (fail-open on absent). */
export function featureOn(
  item: { feature?: FeatureKey | FeatureKey[] },
  features: FeatureFlags | undefined
): boolean {
  if (!item.feature || !features) return true;
  const keys = Array.isArray(item.feature) ? item.feature : [item.feature];
  return keys.every((k) => features[k] !== false);
}

/**
 * The wording to render for an item, given the tenant's flags — `labelWithout`
 * when its named feature is explicitly off, the item's own label otherwise.
 * Fail-open on an absent flag set, exactly like `featureOn`.
 */
export function navLabelOf(
  item: NavLeaf,
  features: FeatureFlags | undefined
): { labelKey: string; label: string } {
  const alt = item.labelWithout;
  if (alt && features && features[alt.feature] === false) {
    return { labelKey: alt.labelKey, label: alt.label };
  }
  return { labelKey: item.labelKey, label: item.label };
}

/**
 * True when the tenant is running the construction terminology profile
 * (login-less site workers, Sites & Camps). Read straight from the branding
 * store so the existing `isVisible`/`allDestinations` call sites (Sidebar,
 * CommandPalette) pick up the profile gate without a signature change. The
 * Sidebar subscribes to `branding`, so it re-renders — and re-runs this
 * filter — whenever branding resolves or changes.
 */
export function isConstructionProfile(): boolean {
  return useBrandingStore.getState().branding?.terminology?.profile === 'construction';
}

/** Profile gate for a single nav item. Items not tagged are always allowed. */
export function profileOn(item: { hideInConstruction?: boolean }): boolean {
  return !item.hideInConstruction || !isConstructionProfile();
}

/** Combined role + feature + profile visibility for a single nav item. */
export function isVisible(
  item: NavLeaf,
  role: Role | undefined,
  features: FeatureFlags | undefined,
  permissions?: readonly string[]
): boolean {
  return canSee(item, role, permissions) && featureOn(item, features) && profileOn(item);
}

/** Active for `/` only on exact match; otherwise on the path or a sub-path. */
export function pathMatches(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * HOW SPECIFICALLY a row claims `pathname`: the length of the address it
 * matched by, or 0. The rail lights the longest claim (Sidebar), so a child
 * route beats its parent.
 *
 * `alsoActiveOn` is for a page with two views at SIBLING addresses — the leave
 * page's queue `/leave/manage` and its "Team calendar" `/leave/calendar`.
 * Neither prefixes the other, so by `to` alone the calendar tab lit no row at
 * all (owner, 2026-10-28). The row names its other view; that address claims
 * as specifically as `to` would, and so outranks `/leave`, which only
 * prefixes it.
 */
export function railClaim(
  item: { to: string; alsoActiveOn?: readonly string[] },
  pathname: string
): number {
  return [item.to, ...(item.alsoActiveOn ?? [])]
    .filter((to) => pathMatches(to, pathname))
    .reduce((best, to) => Math.max(best, to.length), 0);
}

/** The `to` of the row that most specifically claims `pathname`, if any. */
export function activeRowTo(
  items: ReadonlyArray<{ to: string; alsoActiveOn?: readonly string[] }>,
  pathname: string
): string | undefined {
  let best: { to: string; claim: number } | undefined;
  for (const i of items) {
    const claim = railClaim(i, pathname);
    if (claim > 0 && (!best || claim > best.claim)) best = { to: i.to, claim };
  }
  return best?.to;
}
